import { useCallback, useMemo, useState } from 'react'

import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import InfoBanner from '../app/design/InfoBanner'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { TextField } from '../common/fields/TextField'
import { WorkflowIconSection } from '../workflows/WorkflowIconSection'

type Props = {
    isOpen: boolean
    onClose: () => void
    onSuccess: (source: Source) => void
}

export function BankModal({ isOpen, onClose, onSuccess }: Props) {
    const owner = PSApplication.owner()
    const [isSaving, setIsSaving] = useState(false)

    const [source, setSource] = useState<Source>(new Source({ type: 'checking', owner }))
    const [error, setError] = useState<string | null>(null)

    const isAccountNumberValid = useMemo(() => {
        if (!source.accountNumber) return false
        return source.accountNumber?.length >= 6 && source.accountNumber?.length <= 18
    }, [source.accountNumber])

    const isRoutingNumberValid = useMemo(() => {
        if (!source.routingNumber) return false
        return source.routingNumber?.length === 9
    }, [source.routingNumber])

    const isDoneEnabled = useMemo(() => {
        return isAccountNumberValid && isRoutingNumberValid && !!source.name
    }, [isAccountNumberValid, isRoutingNumberValid, source.name])

    const onSave = useCallback(async () => {
        setIsSaving(true)
        const result = await RecordFactory.getInstance().sendSource(source)
        setIsSaving(false)

        if (!result.ok) {
            setError(result.error?.toString() ?? 'An unknown error occurred')
        }
        else {
            onSuccess(result.value)
        }
    }, [onSuccess, source])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Add checking account"
            footer={
                <Button
                    intent="primary"
                    onClick={onSave}
                    disabled={!isDoneEnabled}
                    isLoading={isSaving}
                >
                    Done
                </Button>
            }
        >
            <div className="flex flex-col gap-y-8">
                {error && (
                    <InfoBanner
                        title={'There was an issue adding your account'}
                        subtitle={error}
                        intent="danger"
                        onClose={() => setError(null)}
                    />
                )}
                <WorkflowIconSection title="Name" icon="user.large">
                    <TextField
                        placeholder="Enter account name"
                        size="medium"
                        value={source.name}
                        onChange={(e) => setSource((s) => s.clone({ name: e.target.value }))}
                        onClear={() => setSource((s) => s.clone({ name: '' }))}
                    />
                </WorkflowIconSection>
                <WorkflowIconSection title="Account number" icon="bank.large">
                    <TextField
                        placeholder="Enter account number"
                        size="medium"
                        value={source.accountNumber}
                        onChange={(e) =>
                            setSource((s) => s.clone({ account_number: e.target.value }))
                        }
                        onClear={() => setSource((s) => s.clone({ account_number: '' }))}
                        mask={'000000000000000000'}
                    />
                </WorkflowIconSection>
                <WorkflowIconSection title="Routing number" icon="hash.large">
                    <TextField
                        placeholder="Enter routing number"
                        size="medium"
                        value={source.routingNumber}
                        onChange={(e) =>
                            setSource((s) => s.clone({ routing_number: e.target.value }))
                        }
                        mask={'000000000'}
                        onClear={() => setSource((s) => s.clone({ routing_number: '' }))}
                    />
                </WorkflowIconSection>
                <WorkflowIconSection
                    title="Pay with ACH"
                    icon="dollar.large"
                    hasHoverEffect
                    description='Client payments are direct deposited in the checking account you enter above. "Pay with ACH" lets you pay pros from this account. To get started, two small deposits will be sent to this account for you to verify.'
                    secondaryView={
                        <Checkbox
                            isChecked={source.allowPayment}
                            className="ml-3"
                            onChange={(checked) =>
                                setSource((s) => s.clone({ allow_payment: checked }))
                            }
                        />
                    }
                />
            </div>
        </Modal>
    )
}
